import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft } from 'react-icons/fi';
import {
  AuthForm,
  AuthFormGroup,
  AuthLabel,
  AuthLabelProminent,
  AuthInput,
  AuthInputProminent,
  AuthSelect,
  AuthRow,
  AuthButton,
  AuthErrorMessage,
  AuthHint,
  AuthCheckboxGroup,
  AuthCheckbox,
  AuthCheckboxLabel,
} from './authFormStyles';

const Arrow = FiArrowLeft as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const MIN_AGE = 16;

function getAgeFromDateOfBirth(dateOfBirth: string): number | null {
  if (!dateOfBirth || !dateOfBirth.trim()) return null;
  const d = new Date(dateOfBirth.trim());
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

function getCookiePreferencesFromStorage(): { necessary: boolean; analytics: boolean; marketing: boolean } {
  try {
    const raw = localStorage.getItem('sjmate_cookie_consent');
    if (!raw) return { necessary: true, analytics: false, marketing: false };
    const parsed = JSON.parse(raw);
    return {
      necessary: parsed.necessary !== false,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
    };
  } catch {
    return { necessary: true, analytics: false, marketing: false };
  }
}

const COUNTRY_OPTIONS = [
  { value: '', label: 'Select Country' },
  { value: 'Austria', label: '🇦🇹 - AUSTRIA' },
  { value: 'Bulgaria', label: '🇧🇬 - BULGARIA' },
  { value: 'Canada', label: '🇨🇦 - CANADA' },
  { value: 'China', label: '🇨🇳 - CHINA' },
  { value: 'Czech Republic', label: '🇨🇿 - CZECH REPUBLIC' },
  { value: 'Finland', label: '🇫🇮 - FINLAND' },
  { value: 'France', label: '🇫🇷 - FRANCE' },
  { value: 'Germany', label: '🇩🇪 - GERMANY' },
  { value: 'Hungary', label: '🇭🇺 - HUNGARY' },
  { value: 'Italy', label: '🇮🇹 - ITALY' },
  { value: 'Japan', label: '🇯🇵 - JAPAN' },
  { value: 'South Korea', label: '🇰🇷 - SOUTH KOREA' },
  { value: 'Norway', label: '🇳🇴 - NORWAY' },
  { value: 'Poland', label: '🇵🇱 - POLAND' },
  { value: 'Romania', label: '🇷🇴 - ROMANIA' },
  { value: 'Slovakia', label: '🇸🇰 - SLOVAKIA' },
  { value: 'Slovenia', label: '🇸🇮 - SLOVENIA' },
  { value: 'Sweden', label: '🇸🇪 - SWEDEN' },
  { value: 'Switzerland', label: '🇨🇭 - SWITZERLAND' },
  { value: 'Turkey', label: '🇹🇷 - TURKEY' },
  { value: 'Ukraine', label: '🇺🇦 - UKRAINE' },
  { value: 'United States', label: '🇺🇸 - UNITED STATES' },
  { value: 'Other', label: 'Other' },
];

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
  position: relative;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 3.5rem;
  }
`;

const Card = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: 560px;
  text-align: center;
  background: #ffffff;
  margin: 1.5rem auto;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 1.25rem;
    max-width: 100%;
  }
`;

const Title = styled.h1`
  color: var(--accent-orange);
  margin-top: 0;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: var(--mobile-headline, 1.5rem);
  }
`;

const Subtitle = styled.p`
  color: var(--text-gray);
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: var(--mobile-paragraph, 0.85rem);
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 2rem;
  left: 2rem;
  padding: 0.5rem;
  font-size: 1.2rem;
  border: 2px solid var(--accent-orange);
  border-radius: 50%;
  cursor: pointer;
  background: transparent;
  color: var(--accent-orange);
  transition: var(--transition-normal);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    background: var(--accent-orange);
    color: var(--text-light);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
  }
`;

const AthleteIdBlock = styled.div`
  width: 100%;
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
  padding: 1rem;
  background: rgba(255, 150, 0, 0.06);
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 150, 0, 0.25);

  @media (max-width: 768px) {
    padding: 0.85rem;
  }
`;

const GoogleCompleteSignup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: 'athlete' as 'coach' | 'athlete',
    country: '',
    club: '',
    gender: '' as 'male' | 'female' | '',
    athleteId: '',
    dateOfBirth: '',
    termsAccepted: false,
  });
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    const fetchUserData = async () => {
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/profile`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const userData = await response.json();
            if (userData.firstName) {
              setFormData((prev) => ({ ...prev, firstName: userData.firstName }));
            }
            if (userData.lastName) {
              setFormData((prev) => ({ ...prev, lastName: userData.lastName }));
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    fetchUserData();
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/signin?error=missing_token');
    }
  }, [token, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setFormError('Please fill in first name and last name');
      return false;
    }
    if (!formData.dateOfBirth.trim()) {
      setFormError('Please enter your date of birth');
      return false;
    }
    const age = getAgeFromDateOfBirth(formData.dateOfBirth);
    if (age === null) {
      setFormError('Please enter a valid date of birth');
      return false;
    }
    if (age < MIN_AGE) {
      setFormError('You must be at least 16 years old to use our software.');
      return false;
    }
    if (!formData.termsAccepted) {
      setFormError('You must accept the Terms of Service to create an account.');
      return false;
    }
    if (formData.role === 'athlete' && !formData.gender) {
      setFormError('Please select your gender');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm() || !token) {
      if (!token) setFormError('Missing authentication token');
      return;
    }

    setIsLoading(true);
    try {
      const cookiePrefs = getCookiePreferencesFromStorage();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/google/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            role: formData.role,
            country: formData.country,
            club: formData.club.trim(),
            gender: formData.gender || undefined,
            athleteId: formData.athleteId.trim() || undefined,
            dateOfBirth: formData.dateOfBirth.trim(),
            termsAccepted: true,
            cookiePreferences: cookiePrefs,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to complete signup');
      }

      localStorage.setItem('token', data.token);
      if (data.user.role === 'coach') {
        navigate('/coach/home');
      } else {
        navigate('/jumper/home');
      }
      window.location.reload();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to complete signup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/signin');
  };

  if (!token) {
    return null;
  }

  return (
    <Container>
      <BackButton onClick={handleBack} type="button" aria-label="Go back">
        <Arrow />
      </BackButton>
      <Card>
        <Title>Complete Your Profile</Title>
        <Subtitle>We need a few more details to complete your account setup.</Subtitle>

        {formError && <AuthErrorMessage>{formError}</AuthErrorMessage>}

        <AuthForm onSubmit={handleSubmit}>
          <AuthRow>
            <AuthFormGroup>
              <AuthLabel htmlFor="firstName">First Name</AuthLabel>
              <AuthInput
                id="firstName"
                name="firstName"
                type="text"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="given-name"
                required
              />
            </AuthFormGroup>
            <AuthFormGroup>
              <AuthLabel htmlFor="lastName">Last Name</AuthLabel>
              <AuthInput
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="family-name"
                required
              />
            </AuthFormGroup>
          </AuthRow>

          <AuthFormGroup>
            <AuthLabel htmlFor="dateOfBirth">Date of Birth</AuthLabel>
            <AuthInput
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleChange}
              required
              max={new Date().toISOString().split('T')[0]}
            />
          </AuthFormGroup>

          <AuthFormGroup>
            <AuthLabel htmlFor="role">I am</AuthLabel>
            <AuthSelect
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value="athlete">an Athlete</option>
              <option value="coach">a Coach</option>
            </AuthSelect>
          </AuthFormGroup>

          <AuthRow>
            <AuthFormGroup>
              <AuthLabel htmlFor="country">Country</AuthLabel>
              <AuthSelect
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.value || 'empty'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </AuthSelect>
            </AuthFormGroup>
            <AuthFormGroup>
              <AuthLabel htmlFor="club">Club</AuthLabel>
              <AuthInput
                id="club"
                name="club"
                type="text"
                placeholder="Club name"
                value={formData.club}
                onChange={handleChange}
              />
            </AuthFormGroup>
          </AuthRow>

          {formData.role === 'athlete' && (
            <>
              <AuthFormGroup>
                <AuthLabel htmlFor="gender">Gender</AuthLabel>
                <AuthSelect
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </AuthSelect>
              </AuthFormGroup>

              <AthleteIdBlock>
                <AuthLabelProminent htmlFor="athleteId">Athlete ID</AuthLabelProminent>
                <AuthInputProminent
                  id="athleteId"
                  name="athleteId"
                  type="text"
                  placeholder="Enter your athlete ID to link your account"
                  value={formData.athleteId}
                  onChange={handleChange}
                  autoComplete="off"
                />
                <AuthHint>
                  If you have an athlete ID from your coach, enter it here to link your account.{' '}
                  <Link to="/how-to-start#connecting-accounts" style={{ color: 'var(--accent-orange)', fontWeight: 600, textDecoration: 'underline' }}>
                    Not sure which ID? See connecting accounts
                  </Link>
                </AuthHint>
              </AthleteIdBlock>
            </>
          )}

          <AuthCheckboxGroup>
            <AuthCheckbox
              id="termsAccepted"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
              required
              aria-describedby="terms-hint-google"
            />
            <AuthCheckboxLabel id="terms-hint-google" htmlFor="termsAccepted">
              I agree to the <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and am at least 16 years old.
            </AuthCheckboxLabel>
          </AuthCheckboxGroup>

          <AuthButton type="submit" disabled={isLoading}>
            {isLoading ? 'Completing...' : 'Complete Signup'}
          </AuthButton>
        </AuthForm>
      </Card>
    </Container>
  );
};

export default GoogleCompleteSignup;
